import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Download, Key, Copy, Trash2, Terminal, ShieldCheck, Fingerprint } from "lucide-react";
import QRCode from "qrcode";
import { startRegistration } from "@simplewebauthn/browser";
import { api } from "../lib/api.js";
import { useAuth } from "../auth.jsx";
import { PageHeader, Card, Spinner, timeAgo } from "../components/ui.jsx";
import { AuthenticatorApps } from "../components/AuthenticatorApps.jsx";
import { CopyButton } from "../components/CopyButton.jsx";
import CompanyDetailsCard from "../components/CompanyDetails.jsx";

// Current month + the previous 5, as YYYY-MM (for the invoice picker).
function recentPeriods(n = 6) {
  const out = [];
  const d = new Date();
  for (let i = 0; i < n; i++) {
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`);
    d.setUTCMonth(d.getUTCMonth() - 1);
  }
  return out;
}

// API keys: personal, hashed Bearer tokens. The same token is what the MCP server reads as
// DEBUTDEPLOY_TOKEN. Every key belongs to ONE account and can only see that account's
// servers; an operator can additionally mint a key for a single customer, or — explicitly —
// a fleet key. Fleet is never the default, and it is labelled everywhere it appears.
function ApiKeysCard() {
  const permissionOptions = [
    ["services.read", "Read services, deployments, logs and settings"], ["services.create", "Create and deploy new services"], ["services.manage", "Deploy, change or stop services"],
    ["databases.read", "Read databases and backups"], ["databases.create", "Create databases"], ["databases.manage", "Manage or delete databases"],
    ["email.read", "Read email domains, mailboxes and status"], ["email.create", "Create email addresses and mailboxes"], ["email.manage", "Change, reset or delete email accounts"],
    ["domains.manage", "Add or remove service and mail-hosting domains"], ["hosts.read", "Read host inventory and capacity"], ["hosts.manage", "Change host settings or run host operations"],
    ["billing.read", "Read billing and infrastructure costs"], ["billing.manage", "Change billing, plans or credit and run billable operations"], ["fleet.read", "Operator: read all customer accounts and host capacity"], ["fleet.manage", "Operator: run actions across customer accounts"],
    ["infrastructure.manage", "Admin: create and manage S3 buckets and objects, attach storage to services, register Coolify backup storage, and manage private Compose stacks"],
    ["spend.over", "Allow this key to exceed $50/month (admin approval)"],
  ];
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [keys, setKeys] = useState(null);
  const [name, setName] = useState("");
  const [scope, setScope] = useState("full");
  const [permissions, setPermissions] = useState([]);
  // "own" | "fleet" | "<orgId>" — admins only; a customer's key is always their own account
  const [target, setTarget] = useState("own");
  const [orgs, setOrgs] = useState(null);
  const [fresh, setFresh] = useState(null); // { name, scope, token } — shown ONCE
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = () => api.tokens().then(setKeys).catch(setErr);
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!isAdmin) return;
    api.adminOrgs?.().then((r) => setOrgs(Array.isArray(r) ? r : r?.orgs || [])).catch(() => setOrgs([]));
  }, [isAdmin]);

  const create = async () => {
    setBusy(true); setErr(null);
    try {
      const body = { name: name || "token", scope, permissions: permissions.filter((key) => (!key.startsWith("fleet.") || (isAdmin && target === "fleet")) && (key !== "infrastructure.manage" || (isAdmin && target === "fleet"))) };
      if (isAdmin && target === "fleet") body.fleet = true;
      else if (isAdmin && target !== "own") body.org_id = Number(target);
      const res = await api.createToken(body);
      setFresh(res); setName(""); setScope("full"); setPermissions([]); setTarget("own"); load();
    } catch (e) { setErr(e); } finally { setBusy(false); }
  };
  const revoke = async (id) => {
    if (!confirm("Revoke this key? Anything using it stops working immediately.")) return;
    await api.deleteToken(id); load();
  };

  const origin = window.location.origin;
  const mcpCmd = fresh && `claude mcp add debutdeploy -e DEBUTDEPLOY_URL=${origin} -e DEBUTDEPLOY_TOKEN=${fresh.token} -- node /absolute/path/to/debut-deploy/mcp/server.js`;

  const codexCmd = fresh && `codex mcp add debutdeploy --env DEBUTDEPLOY_URL=${origin} --env DEBUTDEPLOY_TOKEN=${fresh.token} -- node /absolute/path/to/debut-deploy/mcp/server.js`;
  const vscodeConfig = fresh && JSON.stringify({
    inputs: [{ id: "debutdeploy-token", type: "promptString", description: "DebutDeploy API key", password: true }],
    servers: { debutdeploy: {
      type: "stdio", command: "node", args: ["/absolute/path/to/debut-deploy/mcp/server.js"],
      env: { DEBUTDEPLOY_URL: origin, DEBUTDEPLOY_TOKEN: "${input:debutdeploy-token}" },
    } },
  }, null, 2);
  return (
    <Card className="mb-4">
      <div className="flex items-center gap-2 mb-1" style={{ color: "var(--text)" }}>
        <Key size={16} /><span className="text-sm font-semibold">API keys</span>
      </div>
      <div className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>
        Bearer tokens for the REST API and MCP server. Each key is pinned to one account; customer keys cannot see another customer’s services. A <span className="mono">full</span> key can only perform the permissions you select below, while <span className="mono">read-only</span> keys can only make read requests. New MCP keys can add up to $50/month of recurring service, database, mailbox, and disk charges. An administrator must explicitly grant the over-budget permission for higher spend.
      </div>

      {/* Create */}
      <div className="flex flex-wrap gap-2 items-center mb-3">
        <input className="input" placeholder="Key name (e.g. ci, laptop)" value={name}
          onChange={(e) => setName(e.target.value)} style={{ minWidth: 200 }} />
        <select className="input" value={scope} onChange={(e) => setScope(e.target.value)}>
          <option value="full">Full access</option>
          <option value="read">Read-only</option>
        </select>
        {isAdmin && (
          <select className="input" value={target} onChange={(e) => setTarget(e.target.value)} aria-label="Account this key can see">
            <option value="own">My account only</option>
            {(orgs || []).map((o) => <option key={o.id} value={String(o.id)}>Customer: {o.name}</option>)}
            <option value="fleet">⚠ Fleet — every account</option>
          </select>
        )}
        <button className="btn btn-primary" disabled={busy} onClick={create}>{busy ? "Creating…" : "Create key"}</button>
      </div>
      <fieldset className="mb-3 p-3" style={{ border: "1px solid var(--border)", borderRadius: 8 }}>
        <legend className="text-xs font-semibold px-1">Choose what this key can do</legend>
        <p className="text-xs mb-2" style={{ color: "var(--text-muted)" }}>Start with only the boxes the agent needs. No boxes checked means the key can identify its account but has no operational tools. Permissions are checked by the API on every request, so hiding a tool in the agent does not grant or remove access. Create separate keys for separate agents, and revoke a key here if it is lost. The token is shown once after creation; save it in the client’s secret/environment settings, never in source control. Fleet access is only for operator keys and spans all customer accounts.</p>
        <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))" }}>
          {permissionOptions.filter(([key]) => (key !== "spend.over" || isAdmin) && (!key.startsWith("fleet.") || (isAdmin && target === "fleet")) && (key !== "infrastructure.manage" || (isAdmin && target === "fleet"))).map(([key, label]) => <label key={key} className="text-xs flex items-start gap-2" style={{ color: "var(--text)" }}>
            <input type="checkbox" checked={permissions.includes(key)} onChange={(e) => setPermissions((current) => e.target.checked ? [...current, key] : current.filter((p) => p !== key))} />
            <span>{label}</span>
          </label>)}
        </div>
      </fieldset>
      <p className="mb-3 text-xs" style={{ color: "var(--text-muted)" }}>
        Need help choosing permissions? Start with <strong>Read services</strong> for inspection-only agents. Add create/manage permissions only for tasks they should perform. See the <a className="underline" href="https://code.visualstudio.com/docs/agent-customization/mcp-servers" target="_blank" rel="noreferrer">VS Code</a>, <a className="underline" href="https://developers.openai.com/codex/mcp" target="_blank" rel="noreferrer">Codex</a>, or <a className="underline" href="https://code.claude.com/docs/en/mcp" target="_blank" rel="noreferrer">Claude Code</a> setup guide.
      </p>
      {isAdmin && target === "fleet" && (
        <div className="text-xs mb-3 p-2" style={{ color: "var(--err)", border: "1px solid var(--err)", borderRadius: 6 }}>
          A fleet key can read and change <strong>every customer's</strong> services, databases, mail and billing,
          and run commands on the hosts. Only issue one to software you control, and never to a customer.
        </div>
      )}

      {/* One-time reveal */}
      {fresh && (
        <div className="mb-3 p-3" style={{ background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 8 }}>
          <div className="text-xs mb-2" style={{ color: "var(--text-muted)" }}>
            Copy your key now — it won't be shown again.
          </div>
          <div className="flex items-center gap-2 mb-3">
            <input className="input mono" readOnly value={fresh.token} style={{ flex: 1 }} />
            <CopyButton value={fresh.token} />
          </div>
          <details>
            <summary className="text-xs cursor-pointer" style={{ color: "var(--text-muted)" }}>
              <Terminal size={12} style={{ display: "inline", verticalAlign: "-1px" }} /> Connect an AI tool (MCP)
            </summary>
            <pre className="mono mt-2 p-2" style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 6, fontSize: 12, whiteSpace: "pre-wrap", color: "var(--text)" }}>{mcpCmd}</pre>
            <CopyButton className="btn mt-1" value={mcpCmd} label="Copy Claude Code command" />
            <p className="text-xs mt-3" style={{ color: "var(--text-muted)" }}>Codex CLI and Codex IDE extension</p>
            <pre className="mono mt-1 p-2" style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 6, fontSize: 12, whiteSpace: "pre-wrap", color: "var(--text)" }}>{codexCmd}</pre>
            <CopyButton className="btn mt-1" value={codexCmd} label="Copy Codex command" />
            <p className="text-xs mt-3" style={{ color: "var(--text-muted)" }}>These local setup options require Node.js and a trusted local copy of the DebutDeploy MCP package/repository. Install dependencies with <code>cd mcp &amp;&amp; npm install</code>, replace the server path with its absolute path on your machine, and paste the command into a terminal. The command contains your one-time token; avoid shared terminals and shell history. Codex CLI and the Codex IDE extension use the same Codex command.</p>
            <p className="text-xs mt-3" style={{ color: "var(--text-muted)" }}>VS Code with GitHub Copilot Agent mode: create <code>.vscode/mcp.json</code> in your project and use this config. VS Code prompts for the token instead of writing it into the file.</p>
            <pre className="mono mt-1 p-2" style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 6, fontSize: 12, whiteSpace: "pre-wrap", color: "var(--text)" }}>{vscodeConfig}</pre>
            <CopyButton className="btn mt-1" value={vscodeConfig} label="Copy VS Code config" />
            <ol className="mt-3 list-decimal space-y-1 pl-5 text-xs" style={{ color: "var(--text-muted)" }}>
              <li>Install the MCP dependencies and replace the example server path.</li>
              <li>In VS Code, open Copilot Chat in Agent mode and allow the DebutDeploy server when prompted.</li>
              <li>Check the MCP tool picker to confirm the selected permissions are available. Start with <code>whoami</code> to verify the account and budget.</li>
            </ol>
            <p className="text-xs mt-3" style={{ color: "var(--text-muted)" }}>Claude Desktop config uses <code>mcpServers</code>:</p>
            <pre className="mono mt-1 p-2" style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 6, fontSize: 12, whiteSpace: "pre-wrap", color: "var(--text)" }}>{JSON.stringify({ mcpServers: { debutdeploy: { command: "node", args: ["/absolute/path/to/debut-deploy/mcp/server.js"], env: { DEBUTDEPLOY_URL: origin, DEBUTDEPLOY_TOKEN: fresh.token } } } }, null, 2)}</pre>
            <p className="text-xs mt-3" style={{ color: "var(--text-muted)" }}>Remote MCP: a separately hosted Streamable HTTP MCP endpoint is required for web clients. Run the MCP server behind HTTPS with <code>MCP_TRANSPORT=streamable-http</code>, a strict <code>MCP_ALLOWED_HOSTS</code> allowlist, and port <code>8788</code> forwarded to <code>/mcp</code>. The DebutDeploy panel URL by itself is not an MCP endpoint. Configure a per-account bearer token in each client. This deployment does not provide OAuth for remote MCP, so do not assume Claude web or ChatGPT web can connect directly.</p>
            <pre className="mono mt-1 p-2" style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 6, fontSize: 12, whiteSpace: "pre-wrap", color: "var(--text)" }}>{`# ~/.codex/config.toml\n[mcp_servers.debutdeploy]\nurl = "https://mcp.example.com/mcp"\nbearer_token_env_var = "DEBUTDEPLOY_MCP_TOKEN"\n\n# Set DEBUTDEPLOY_MCP_TOKEN to your API key in your environment.`}</pre>
            <p className="text-xs mt-2" style={{ color: "var(--text-muted)" }}>For Claude Code, use the command above. Claude Desktop uses the local config below. Claude web and ChatGPT web need a deployed remote MCP server and compatible authentication; those are not configured by this local command. Follow the current client instructions before connecting: <a className="underline" href="https://code.visualstudio.com/docs/agent-customization/mcp-servers" target="_blank" rel="noreferrer">VS Code MCP setup</a> · <a className="underline" href="https://developers.openai.com/codex/mcp" target="_blank" rel="noreferrer">Codex MCP setup</a> · <a className="underline" href="https://docs.anthropic.com/en/docs/claude-code/mcp" target="_blank" rel="noreferrer">Claude Code MCP setup</a>.</p>
          </details>
        </div>
      )}

      {err && <div className="text-xs mb-2" style={{ color: "var(--err)" }}>{err.message}</div>}

      {/* List */}
      {!keys && <div className="text-xs" style={{ color: "var(--text-muted)" }}><Spinner className="mr-2" /> Loading…</div>}
      {keys && keys.length === 0 && <div className="text-xs" style={{ color: "var(--text-muted)" }}>No keys yet.</div>}
      {keys && keys.length > 0 && (
        <table className="w-full text-sm" style={{ borderCollapse: "collapse" }}>
          <thead><tr style={{ color: "var(--text-muted)", textAlign: "left" }}>
            <th className="py-2 font-semibold">Name</th>
            <th className="py-2 font-semibold">Account</th>
            <th className="py-2 font-semibold">Scope</th>
            <th className="py-2 font-semibold">Permissions</th>
            <th className="py-2 font-semibold">Last used</th>
            <th className="py-2 font-semibold"></th>
          </tr></thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.id} style={{ borderTop: "1px solid var(--border)" }}>
                <td className="py-2" style={{ color: "var(--text)" }}>{k.name || "—"}</td>
                <td className="py-2">
                  {k.fleet
                    ? <span className="pill" style={{ background: "var(--err)", color: "#fff" }} title="Can see and change every account">Fleet · all accounts</span>
                    : <span style={{ color: "var(--text-muted)" }}>{k.org_name || (k.org_id ? `Account ${k.org_id}` : "—")}</span>}
                </td>
                <td className="py-2"><span className={`pill ${k.scope === "read" ? "pill-neutral" : "pill-accent"}`}>{k.scope === "read" ? "Read-only" : "Full"}</span></td>
                <td className="py-2 text-xs" style={{ color: "var(--text-muted)", maxWidth: 290 }}>{k.permissions === null ? "Legacy key policy" : k.permissions.length ? k.permissions.join(", ") : "identity only"}<br />Monthly commitment ${Number(k.committed_usd || 0).toFixed(2)} / $50</td>
                <td className="py-2" style={{ color: "var(--text-muted)" }}>{k.last_used_at ? timeAgo(k.last_used_at) : "never"}</td>
                <td className="py-2 text-right"><button className="btn btn-danger" onClick={() => revoke(k.id)}><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function AuthenticatorCard() {
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);
  const [qrImage, setQrImage] = useState(null);
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const load = () => api.totpStatus().then(setStatus).catch((e) => setError(e.message));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    let current = true;
    setQrImage(null);
    if (setup?.otpauthUrl) QRCode.toDataURL(setup.otpauthUrl, { width: 208, margin: 2, errorCorrectionLevel: "M" })
      .then((url) => { if (current) setQrImage(url); })
      .catch(() => { if (current) setQrImage(""); });
    return () => { current = false; };
  }, [setup]);
  const start = async () => {
    setBusy(true); setError("");
    try { setSetup(await api.totpSetup()); setCode(""); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const enable = async () => {
    setBusy(true); setError("");
    try { const result = await api.totpEnable(code); setRecovery(result.recoveryCodes); setSetup(null); setCode(""); await load(); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true); setError("");
    try { await api.totpDisable(code); setCode(""); setRecovery(null); await load(); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  return <Card className="mb-4">
    <div className="flex items-center gap-2 mb-1" style={{ color: "var(--text)" }}><ShieldCheck size={16} /><span className="text-sm font-semibold">Authenticator app</span></div>
    <div className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>Add a time-based one-time password app as a second step when signing in with Google or GitHub.</div>
    {!status && !error && <div className="text-xs" style={{ color: "var(--text-muted)" }}><Spinner className="mr-2" /> Loading…</div>}
    {status && !status.enabled && !setup && <button className="btn btn-primary" disabled={busy} onClick={start}>{busy ? "Starting…" : "Set up authenticator"}</button>}
    {setup && <div className="mt-3">
      <p className="text-xs mb-2" style={{ color: "var(--text-muted)" }}>Scan this QR code with your authenticator app, or enter the setup key below. Keep the key private.</p>
      <AuthenticatorApps className="mb-3" />
      {qrImage && <div className="mb-3" style={{ display: "inline-block", padding: 8, background: "#fff", borderRadius: 8 }}><img src={qrImage} width="208" height="208" alt="Authenticator setup QR code" /></div>}
      <div className="flex flex-wrap gap-2 items-center mb-3"><code className="mono p-2" style={{ color: "var(--text)", background: "var(--surface-2)", borderRadius: 6 }}>{setup.secret}</code><CopyButton value={setup.secret} label="Copy key" /></div>
      <div className="flex gap-2"><input className="input mono" inputMode="numeric" autoComplete="one-time-code" placeholder="6-digit code" value={code} onChange={(e) => setCode(e.target.value)} /><button className="btn btn-primary" disabled={busy || code.length < 6} onClick={enable}>Confirm and enable</button></div>
    </div>}
    {status?.enabled && <div className="mt-2">
      <div className="text-xs mb-2" style={{ color: "var(--text-muted)" }}>Enabled. {status.recoveryCodesRemaining} unused recovery codes remain. Keep recovery codes somewhere private.</div>
      {recovery && <div className="p-3 mb-3" style={{ background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 6 }}><div className="text-xs mb-2" style={{ color: "var(--text)" }}>Save these recovery codes now. They are shown once.</div><pre className="mono text-xs" style={{ color: "var(--text)", whiteSpace: "pre-wrap" }}>{recovery.join("\n")}</pre><CopyButton className="btn mt-2" value={recovery.join("\n")} label="Copy codes" /></div>}
      <div className="flex gap-2"><input className="input mono" inputMode="numeric" autoComplete="one-time-code" placeholder="Current 6-digit code" value={code} onChange={(e) => setCode(e.target.value)} /><button className="btn btn-danger" disabled={busy || code.length < 6} onClick={disable}>Disable</button></div>
    </div>}
    {error && <div role="alert" className="text-xs mt-2" style={{ color: "var(--err)" }}>{error}</div>}
  </Card>;
}

function PasskeysCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const load = () => api.passkeys().then(setStatus).catch((e) => setError(e.message));
  useEffect(() => { load(); }, []);
  const add = async () => {
    setBusy(true); setError("");
    try {
      const options = await api.passkeyRegistrationOptions();
      const credential = await startRegistration({ optionsJSON: options });
      await api.verifyPasskeyRegistration(options.challenge, credential);
      await load();
    } catch (e) { setError(e.message || "Could not add passkey."); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!confirm("Remove this passkey from your account?")) return;
    setBusy(true); setError("");
    try { await api.deletePasskey(id); await load(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  return <Card className="mb-4">
    <div className="flex items-center gap-2 mb-1" style={{ color: "var(--text)" }}><Fingerprint size={16} /><span className="text-sm font-semibold">Passkeys and device biometrics</span></div>
    <div className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>Register this device to sign in with Face ID, Touch ID, Windows Hello, a device PIN, or a security key. Passkeys require HTTPS.</div>
    {!status && !error && <div className="text-xs" style={{ color: "var(--text-muted)" }}><Spinner className="mr-2" /> Loading…</div>}
    {status?.credentials?.map((credential) => <div key={credential.credential_id} className="flex flex-wrap items-center justify-between gap-2 py-2" style={{ borderBottom: "1px solid var(--border)" }}>
      <div className="text-xs" style={{ color: "var(--text)" }}>Passkey · added {timeAgo(credential.created_at)}</div>
      <button className="btn btn-danger" disabled={busy} onClick={() => remove(credential.credential_id)}><Trash2 size={13} /> Remove</button>
    </div>)}
    {status?.available && <button className="btn btn-primary mt-3" disabled={busy} onClick={add}>{busy ? "Waiting for device…" : "Add a passkey"}</button>}
    {status && !status.available && <p className="text-xs" style={{ color: "var(--err-text)" }}>Passkeys are unavailable until the site origin and RP ID are configured for HTTPS.</p>}
    {error && <div role="alert" className="text-xs mt-2" style={{ color: "var(--err-text)" }}>{error}</div>}
  </Card>;
}

export default function Settings() {
  const { user } = useAuth();
  const [period, setPeriod] = useState(recentPeriods(1)[0]);

  return (
    <div className="page">
      <PageHeader title="Account settings" subtitle="Your profile, company details, and invoices." />

      {/* Profile */}
      <Card className="mb-4">
        <div className="text-sm font-semibold mb-3" style={{ color: "var(--text)" }}>Profile</div>
        <div className="grid gap-1 text-sm" style={{ gridTemplateColumns: "120px 1fr", maxWidth: 460 }}>
          <div style={{ color: "var(--text-muted)" }}>Name</div><div style={{ color: "var(--text)" }}>{user?.name || "—"}</div>
          <div style={{ color: "var(--text-muted)" }}>Email</div><div className="mono" style={{ color: "var(--text)" }}>{user?.email}</div>
          <div style={{ color: "var(--text-muted)" }}>Role</div><div><span className="pill pill-neutral">{user?.orgRole || user?.role}</span></div>
        </div>
      </Card>

      <AuthenticatorCard />
      <PasskeysCard />

      {/* API keys */}
      <ApiKeysCard />

      {user?.role === "admin" && <Card className="mb-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold" style={{ color: "var(--text)" }}>Object Storage</div>
            <p className="mt-1 text-xs" style={{ color: "var(--text-muted)" }}>Manage Hetzner buckets and files, set browser CORS, and attach bucket settings to deployed services.</p>
          </div>
          <Link className="btn btn-primary" to="/object-storage">Manage Object Storage</Link>
        </div>
      </Card>}

      {user?.role === "admin" && <Card className="mb-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold" style={{ color: "var(--text)" }}>Private Compose stacks</div>
            <p className="mt-1 text-xs" style={{ color: "var(--text-muted)" }}>Review and edit Coolify Compose definitions, then deploy them separately.</p>
          </div>
          <Link className="btn btn-primary" to="/compose">Manage Compose stacks</Link>
        </div>
      </Card>}

      {/* Company details + email / phone verification (also the invoice details) */}
      <CompanyDetailsCard />

      {/* Invoices */}
      <Card>
        <div className="text-sm font-semibold mb-3" style={{ color: "var(--text)" }}>Invoices</div>
        <div className="flex flex-wrap items-center gap-2">
          <select className="input" value={period} onChange={(e) => setPeriod(e.target.value)}>
            {recentPeriods(12).map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <a className="btn" href={api.orgInvoiceUrl(period)} target="_blank" rel="noreferrer"><FileText size={14} /> View</a>
          <a className="btn" href={api.orgInvoiceUrl(period, true)}><Download size={14} /> Download</a>
        </div>
        <div className="text-xs mt-2" style={{ color: "var(--text-muted)" }}>Opens a printable invoice — use your browser's “Save as PDF”.</div>
      </Card>
    </div>
  );
}
